import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import toast from "react-hot-toast"

import { FiArrowLeft } from "react-icons/fi"

import Header from "../../component/header/header"
import { TiptapEditor } from "../../component/tiptapEditor"
import QuestionArtifacts from "../../component/questionArtifacts/QuestionArtifacts"
import Button from "../../component/button/button"
import { LoaderComponent } from "../../utils/Helper"
import { editSingleQuestion } from "../../api/question"
import { QuestionStatus } from "../../api/type"
import { GRADES } from "../../utils/grade"
import { complexities } from "../../utils/complexity"
import { buildSubParts, formatSubPartsLabels } from "../../utils/subParts"
import { getYearValidationError, parseYearOrNull } from "../../utils/yearValidation"
import QuestionConfirmModals from "./components/QuestionConfirmModals"
import QuestionDetailActions from "./components/QuestionDetailActions"
import QuestionDetailPanel from "./components/QuestionDetailPanel"
import QuestionDetailSidebar from "./components/QuestionDetailSidebar"
import QuestionDetailSidebarEditable from "./components/QuestionDetailSidebarEditable"
import { LevelFieldErrors } from "./components/LevelSelectors"
import { useQuestionActions } from "./hooks/useQuestionActions"
import { useQuestionDetail } from "./hooks/useQuestionDetail"
import { useQuestionSubjects } from "./hooks/useQuestionSubjects"
import { QuestionInlineDraft } from "./types/questionDraft"
import {
  DEFAULT_LEVEL_LABELS,
  normalizeLabels,
} from "../Subjects/detail/hierarchyUtils"
import {
  hasLevelFieldErrors,
  validateQuestionHierarchyLevels,
} from "./utils/hierarchyValidation"
import "./QuestionDetail.style.scss"

type InlineTaxonomyErrors = {
  subject?: string
  levels?: LevelFieldErrors
  year?: string
}

const getInlineTaxonomyErrors = (
  draft: QuestionInlineDraft
): InlineTaxonomyErrors => {
  const errors: InlineTaxonomyErrors = {}

  if (!draft.subject?.value) {
    errors.subject = "Subject is required."
  } else {
    const labels = normalizeLabels(
      draft.subject.level_labels && draft.subject.level_labels.length
        ? draft.subject.level_labels
        : DEFAULT_LEVEL_LABELS
    )
    const levelErrors = validateQuestionHierarchyLevels(draft.levels, labels)
    if (hasLevelFieldErrors(levelErrors)) {
      errors.levels = levelErrors
    }
  }

  const yearError = getYearValidationError(draft.year)
  if (yearError) errors.year = yearError

  return errors
}

const QuestionDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { question, loading, patchQuestion } = useQuestionDetail(id)
  const { subjects, subjectById } = useQuestionSubjects()
  const [isInlineEditing, setIsInlineEditing] = useState(false)
  const [isSavingInline, setIsSavingInline] = useState(false)
  const [inlineTaxonomyErrors, setInlineTaxonomyErrors] =
    useState<InlineTaxonomyErrors>({})

  const [draft, setDraft] = useState<QuestionInlineDraft | null>(null)

  const {
    confirmType,
    pendingStatus,
    pendingLearnerValue,
    pendingVerification,
    isConfirmLoading,
    closeConfirm,
    handleConfirm,
    openLearnerPublishConfirm,
    openDeleteConfirm,
    openPublishConfirm,
    openArchiveConfirm,
    openRestoreConfirm,
    openVerifyConfirm,
  } = useQuestionActions({
    onUpdate: (questionId, patch) => {
      if (questionId === id) {
        patchQuestion(patch)
      }
    },
    onDeleteSuccess: () => navigate("/questions"),
  })

  if (loading) {
    return (
      <div className="content question-detail">
        <LoaderComponent label="Loading question…" />
      </div>
    )
  }

  if (!question) return null

  const subjectLabel =
    question.subject_title?.trim() ||
    subjectById(question.subject)?.label ||
    null

  const startInlineEdit = () => {
    const selectedSubject = subjectById(question.subject)
    setInlineTaxonomyErrors({})
    setDraft({
      subject: selectedSubject,
      levels: {
        level_1: question.topic_id ?? "",
        level_2: question.level_2_id ?? "",
        level_3: question.level_3_id ?? "",
        level_4: question.level_4_id ?? "",
      },
      levelNames: {
        level_1: question.topic ?? null,
        level_2: question.level_2_name ?? null,
        level_3: question.level_3_name ?? null,
        level_4: question.level_4_name ?? null,
      },
      grade:
        GRADES.find((item) => item.value === question.grade) ??
        (question.grade ? { label: question.grade, value: question.grade } : null),
      complexity:
        complexities.find((item) => item.value === question.complexity) ??
        (question.complexity
          ? { label: question.complexity, value: question.complexity }
          : null),
      source: question.source ?? "",
      year: question.year != null ? String(question.year) : "",
      marks: question.marks != null ? String(question.marks) : "",
      question: question.question ?? "",
      answer: question.answer ?? "",
      solution: question.solution ?? "",
      marking_scheme: question.marking_scheme ?? "",
      question_status: (question.question_status ?? "draft") as QuestionStatus,
      subPartsText: formatSubPartsLabels(question.sub_parts),
      preservedSubParts: question.sub_parts ?? [],
    })
    setIsInlineEditing(true)
  }

  const cancelInlineEdit = () => {
    setIsInlineEditing(false)
    setDraft(null)
    setInlineTaxonomyErrors({})
  }

  const saveInlineEdit = async () => {
    if (!question.id || !draft) return

    const taxonomyErrors = getInlineTaxonomyErrors(draft)
    if (Object.keys(taxonomyErrors).length > 0) {
      setInlineTaxonomyErrors(taxonomyErrors)
      return
    }

    setIsSavingInline(true)
    try {
      await editSingleQuestion(question.id, {
        subject: draft.subject?.value ?? question.subject,
        topic_id: draft.levels.level_1 || null,
        level_2_id: draft.levels.level_2 || null,
        level_3_id: draft.levels.level_3 || null,
        level_4_id: draft.levels.level_4 || null,
        grade: draft.grade?.value ?? question.grade,
        complexity: draft.complexity?.value ?? question.complexity,
        source: draft.source.trim() || null,
        year: parseYearOrNull(draft.year),
        marks: draft.marks ? Number(draft.marks) : null,
        question: draft.question,
        answer: draft.answer,
        solution: draft.solution,
        marking_scheme: draft.marking_scheme,
        question_status: draft.question_status,
        sub_parts: buildSubParts(draft.subPartsText, draft.preservedSubParts),
      })
      patchQuestion({
        subject: draft.subject?.value ?? question.subject,
        topic_id: draft.levels.level_1 || null,
        topic: draft.levelNames.level_1 ?? null,
        level_2_id: draft.levels.level_2 || null,
        level_2_name: draft.levelNames.level_2 ?? null,
        level_3_id: draft.levels.level_3 || null,
        level_3_name: draft.levelNames.level_3 ?? null,
        level_4_id: draft.levels.level_4 || null,
        level_4_name: draft.levelNames.level_4 ?? null,
        grade: draft.grade?.value ?? question.grade,
        complexity: draft.complexity?.value ?? question.complexity,
        source: draft.source.trim() || null,
        year: parseYearOrNull(draft.year),
        marks: draft.marks ? Number(draft.marks) : null,
        question: draft.question,
        answer: draft.answer,
        solution: draft.solution,
        marking_scheme: draft.marking_scheme,
        question_status: draft.question_status,
        sub_parts: buildSubParts(draft.subPartsText, draft.preservedSubParts),
      })
      setIsInlineEditing(false)
      setDraft(null)
      setInlineTaxonomyErrors({})
    } catch (error) {
      console.error(error)
      toast.error("Unable to save the question. Please try again.")
    } finally {
      setIsSavingInline(false)
    }
  }

  return (
    <div className="content question-detail">
      <Header
        pageTitle="Question Details"
        buttonText="All questions"
        buttonIcon={<FiArrowLeft size={18} />}
        type="secondary"
        onClick={() => navigate("/questions")}
        extraButton={
          <QuestionDetailActions
            question={question}
            isEditing={isInlineEditing}
            onEdit={() => startInlineEdit()}
            onPreview={cancelInlineEdit}
            onPublish={() => openPublishConfirm(question)}
            onMoveToDraft={() => openRestoreConfirm(question, "draft")}
            onArchive={() => openArchiveConfirm(question)}
            onActivate={() => openRestoreConfirm(question, "published")}
            onToggleLearnerPublish={() =>
              openLearnerPublishConfirm(
                question,
                !question.published_to_learner
              )
            }
            onVerify={() => openVerifyConfirm(question, "verified")}
            onReject={() => openVerifyConfirm(question, "rejected")}
            onDelete={() => question.id && openDeleteConfirm(question.id)}
          />
        }
      />

      <div className="question-detail__layout">
        {isInlineEditing && draft ? (
          <QuestionDetailSidebarEditable
            draft={draft}
            onChange={(updater) =>
              setDraft((prev) => (prev ? updater(prev) : prev))
            }
            subjects={subjects}
            fieldErrors={inlineTaxonomyErrors}
            onLevelChange={() =>
              setInlineTaxonomyErrors((prev) => ({ ...prev, levels: undefined }))
            }
            onSubjectChange={() =>
              setInlineTaxonomyErrors((prev) => ({
                ...prev,
                subject: undefined,
                levels: undefined,
              }))
            }
          />
        ) : (
          <QuestionDetailSidebar
            question={question}
            subjectLabel={subjectLabel}
            levelLabels={subjectById(question.subject)?.level_labels}
          />
        )}
        {isInlineEditing && draft ? (
          <div className="question-detail__main">
            <div className="question-detail__section">
              <div className="question-detail__section-header">
                <span className="question-detail__section-label">Question</span>
              </div>
              <div className="question-detail__section-body">
                <TiptapEditor
                  value={draft.question}
                  id="inline_question_editor"
                  handleOnChange={(value) =>
                    setDraft((prev) => (prev ? { ...prev, question: value } : prev))
                  }
                  label=""
                  height={320}
                  placeholder="Enter the question…"
                />
                <QuestionArtifacts artifacts={question.question_artifacts} />
              </div>
            </div>

            <div className="question-detail__section">
              <div className="question-detail__section-header">
                <span className="question-detail__section-label">Answer</span>
              </div>
              <div className="question-detail__section-body">
                <TiptapEditor
                  value={draft.answer}
                  id="inline_answer_editor"
                  handleOnChange={(value) =>
                    setDraft((prev) => (prev ? { ...prev, answer: value } : prev))
                  }
                  label=""
                  height={220}
                  placeholder="Enter the answer…"
                />
              </div>
            </div>

            <div className="question-detail__section">
              <div className="question-detail__section-header">
                <span className="question-detail__section-label">Solution</span>
              </div>
              <div className="question-detail__section-body">
                <TiptapEditor
                  value={draft.solution}
                  id="inline_solution_editor"
                  handleOnChange={(value) =>
                    setDraft((prev) => (prev ? { ...prev, solution: value } : prev))
                  }
                  label=""
                  height={260}
                  placeholder="Enter the full solution…"
                />
                <QuestionArtifacts artifacts={question.solution_artifacts} />
              </div>
            </div>

            <div className="question-detail__section">
              <div className="question-detail__section-header">
                <span className="question-detail__section-label">Marking scheme</span>
              </div>
              <div className="question-detail__section-body">
                <TiptapEditor
                  value={draft.marking_scheme}
                  id="inline_marking_scheme_editor"
                  handleOnChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, marking_scheme: value } : prev
                    )
                  }
                  label=""
                  height={220}
                  placeholder="Describe how marks are awarded…"
                />
              </div>
            </div>

            <div className="question-detail__actions">
              <Button
                text="Cancel"
                type="secondary"
                disabled={isSavingInline}
                onClick={cancelInlineEdit}
              />
              <Button
                text="Save changes"
                type="primary"
                isLoading={isSavingInline}
                loadingText="Saving…"
                disabled={isSavingInline}
                onClick={saveInlineEdit}
              />
            </div>
          </div>
        ) : (
          <QuestionDetailPanel question={question} />
        )}
      </div>

      <QuestionConfirmModals
        confirmType={confirmType}
        pendingStatus={pendingStatus}
        pendingLearnerValue={pendingLearnerValue}
        pendingVerification={pendingVerification}
        isLoading={isConfirmLoading}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  )
}

export default QuestionDetail
